import type { BikeTelemetry } from '../../sim/bike';
import { FeatureDef, featureLength, isJump, jumpSpan } from '../../sim/features';
import type { Level } from '../../sim/level';
import type { LevelId } from '../../sim/levels';
import type { DrillId, DrillResult } from '../drill';

/**
 * What went wrong, in the words a coach would use. Crash causes come from the sim's fall cause plus the moment
 * around it (lean, landing attitude, what was hit); drill causes from the drill's star reasons; `cased` /
 * `overshot` from where a jump's landing touched down; `slow` from a sector time.
 */
export type HowToCause =
  | 'washout' | 'lowSide' | 'overBars' | 'loopOut' | 'noseLow' | 'noseHigh' | 'hitTree' | 'hitRock' | 'hitObstacle' | 'tipped'
  | 'cased' | 'overshot' | 'slow'
  | 'wheelspin' | 'wheelie' | 'leftLane' | 'brakeEarly' | 'brakeLate' | 'frontLock' | 'tooSlow' | 'cones' | 'stance' | 'foot' | 'fell';

export const HOWTO_CAUSES: readonly HowToCause[] = [
  'washout', 'lowSide', 'overBars', 'loopOut', 'noseLow', 'noseHigh', 'hitTree', 'hitRock', 'hitObstacle', 'tipped',
  'cased', 'overshot', 'slow', 'wheelspin', 'wheelie', 'leftLane', 'brakeEarly', 'brakeLate', 'frontLock', 'tooSlow',
  'cones', 'stance', 'foot', 'fell',
];

/** A place on a level where a demo can be ridden: a drill, a track feature, or a stretch of track. */
export interface Situation {
  level: LevelId;
  cause: HowToCause;
  drill?: DrillId;
  /** Arc length (m) of the spot on the level's track. */
  s?: number;
  /** Feature id (`jump@59`, `berm@212`, `log@450`, `sector@3`); absent on open track. */
  feature?: string;
}

/** Where on the track a demo is anchored: the feature's start, or the spot itself rounded to 25 m. */
export function situationSpot(s: Situation): string {
  if (s.drill) return `drill:${s.drill}`;
  return s.feature ?? `s${Math.round((s.s ?? 0) / 25) * 25}`;
}

/** Offered at most once per key per session; same key = same demo. */
export const situationKey = (s: Situation): string => `${s.level}:${situationSpot(s)}:${s.cause}`;

export const featureId = (f: FeatureDef): string => `${isJump(f) ? 'jump' : f.kind}@${f.s}`;

/** Metres before a feature and after it where a mistake still belongs to it (the approach and the run-out). */
const BEFORE = 15, AFTER = 20;

/** The feature `s` belongs to, nearest first. */
export function featureAt(level: Level, s: number): FeatureDef | null {
  const line = level.line;
  if (!line) return null;
  let best: FeatureDef | null = null, bestD = Infinity;
  for (const f of level.features) {
    let u = s - f.s;
    if (u > line.length / 2) u -= line.length; else if (u < -line.length / 2) u += line.length;
    const len = featureLength(f);
    if (u < -BEFORE || u > len + AFTER) continue;
    const d = u < 0 ? -u : u > len ? u - len : 0;
    if (d < bestD) { best = f; bestD = d; }
  }
  return best;
}

export function findFeature(level: Level, id: string): FeatureDef | null {
  return level.features.find((f) => featureId(f) === id) ?? null;
}

/** Where a touchdown at `s` put the bike on a jump: short of the landing (cased), past its slope (overshot), or fine. */
export function jumpLandingCause(f: FeatureDef, s: number): 'cased' | 'overshot' | null {
  if (!isJump(f)) return null;
  const u = s - f.s, span = jumpSpan(f);
  if (u > span.lip && u < span.land - 0.3) return 'cased';
  if (u > featureLength(f) + 1) return 'overshot';
  return null;
}

/** Crash cause from the sim's fall cause and the telemetry of the fall step. */
export function crashCause(t: Readonly<BikeTelemetry>, braking: boolean): HowToCause {
  switch (t.fallCause) {
    case 'overBars': return 'overBars';
    case 'washout': return Math.abs(t.roll) > 0.45 && !braking ? 'lowSide' : 'washout';
    case 'spin': return 'lowSide';
    case 'landing': return (t.lastLanding?.noseError ?? 0) > 0 ? 'noseHigh' : 'noseLow';
    case 'hit': return t.hitInfo.kind === 'tree' ? 'hitTree' : t.hitInfo.kind === 'rock' || t.hitInfo.kind === 'stone' ? 'hitRock' : 'hitObstacle';
    case 'tipped': return t.pitch > 0.9 ? 'loopOut' : 'tipped';
    default: return 'fell';
  }
}

/** A fall on a track level at arc length `s`: which feature, and which cause (a crash landing on a jump reads as cased / overshot). */
export function crashSituation(level: Level, s: number, t: Readonly<BikeTelemetry>, braking: boolean, landedAt: number | null): Situation {
  const f = featureAt(level, s);
  let cause = crashCause(t, braking);
  if (f && t.fallCause === 'landing' && landedAt !== null) cause = jumpLandingCause(f, landedAt) ?? cause;
  return { level: level.def.id as LevelId, cause, s: f ? f.s : s, ...(f ? { feature: featureId(f) } : {}) };
}

/** A hard landing (no fall) on a jump that was cased or overshot; null when it was fine or not on a jump. */
export function landingSituation(level: Level, s: number): Situation | null {
  const f = featureAt(level, s);
  if (!f) return null;
  const cause = jumpLandingCause(f, s);
  return cause ? { level: level.def.id as LevelId, cause, s: f.s, feature: featureId(f) } : null;
}

/** Drill result -> situation: the first star-costing reason decides, a perfect run has none. */
export function drillSituation(drill: DrillId, r: DrillResult): Situation | null {
  if (r.stars >= 3) return null;
  const reason = r.reasons[0];
  const cause = drillCause(drill, reason, r);
  return cause ? { level: 'ring', drill, cause } : null;
}

function drillCause(drill: DrillId, reason: string | undefined, r: DrillResult): HowToCause | null {
  switch (reason) {
    case 'result.reason.fallen': return 'fell';
    case 'result.reason.rearSlip': return 'wheelspin';
    case 'result.reason.wheelie': return 'wheelie';
    case 'result.reason.leftLane': case 'result.reason.leftArea': return 'leftLane';
    case 'result.reason.frontLock': return 'frontLock';
    case 'result.reason.tooSlow': case 'result.reason.slow': case 'result.reason.timeout': return 'tooSlow';
    case 'result.reason.missedCone': case 'result.reason.hitCone': return 'cones';
    case 'result.reason.stance': return 'stance';
    case 'result.reason.distance': {
      // Stopped short of the line = braked early; past it = late (the brake drill's signed `past`).
      return (r.metrics.past ?? 0) > 0 ? 'brakeLate' : 'brakeEarly';
    }
    default: return drill === 'figure8' ? 'foot' : null;
  }
}

/** Sector slower than `factor` x the reference time: a slow sector. */
export function slowSector(level: Level, index: number, sectorTime: number, refTime: number, factor = 1.6): Situation | null {
  if (!(refTime > 0) || sectorTime < refTime * factor) return null;
  const cp = level.checkpoints[index];
  if (!cp) return null;
  return { level: level.def.id as LevelId, cause: 'slow', s: cp.s, feature: `sector@${index}` };
}
